/**
 * vc-shared.js — the pure recipe of on-device voice conversion (per-voice RVC v2),
 * one copy for the worker, the page facade and the unit tests (window/self.VcShared).
 *
 * Reference: vcbench/vg/rvc_onnx_pipeline.py convert_browser — the Python recipe the bench
 * scored; the page lands on it sample for sample (18.10.2026, tests/playwright parity run:
 * WebGPU 69 dB, WASM 113 dB on 7 s; 60 / 102 dB on 38 s across three windows).
 *
 *   16 kHz mono → high-pass 48 Hz (Butterworth 5, SOS, forward) → peak ≤ 0.95
 *   → reflect pad 1 s each side
 *   → f0 from RmvpePitch (10 ms hop, unvoiced where conf < 0.03) × 2^(st/12)
 *   → coarse pitch 1..255 (RVC's mel map, f0 50..1100 Hz)
 *   → ContentVec [1,T,768] (20 ms) → each frame twice (10 ms, = Applio's ×2 nearest)
 *   → generator with noise drawn here BY ABSOLUTE POSITION (prior N(0,1) [1,192,L], NSF
 *     N(0,1) [1,L·upp,1]) and the NSF sine's phase carried in (phase0)
 *   → 40 kHz, windows joined by 20 ms crossfades, the 1 s pads cut off.
 */
(function (root) {
'use strict';

var SR_IN = 16000, HOP = 160, PAD_S = 1;
// scipy.signal.butter(5, 48, btype='high', fs=16000, output='sos')
var SOS_HP = [
    [0.9699606451838447, -0.9699606451838447, 0.0, 1.0, -0.9813258904926881, 0.0],
    [1.0, -2.0, 1.0, 1.0, -1.9696106864374547, 0.9699606452559844],
    [1.0, -2.0, 1.0, 1.0, -1.9880652422382208, 0.9884184800471566]
];
var F0_MIN = 50, F0_MAX = 1100;
var MEL_MIN = 1127 * Math.log(1 + F0_MIN / 700), MEL_MAX = 1127 * Math.log(1 + F0_MAX / 700);
var UNVOICED_CONF = 0.03;

/** Butterworth 5 high-pass, direct form II transposed per section, forward only. */
function highpass(x) {
    var y = new Float32Array(x.length);
    y.set(x);
    for (var s = 0; s < SOS_HP.length; s++) {
        var b0 = SOS_HP[s][0], b1 = SOS_HP[s][1], b2 = SOS_HP[s][2], a1 = SOS_HP[s][4], a2 = SOS_HP[s][5];
        var z1 = 0, z2 = 0;
        for (var i = 0; i < y.length; i++) {
            var xi = y[i], yi = b0 * xi + z1;
            z1 = b1 * xi - a1 * yi + z2;
            z2 = b2 * xi - a2 * yi;
            y[i] = yi;
        }
    }
    return y;
}

function peakLimit(x, ceiling) {
    var p = 0;
    for (var i = 0; i < x.length; i++) { var a = Math.abs(x[i]); if (a > p) p = a; }
    var k = p / (ceiling || 0.95);
    if (k > 1) for (var j = 0; j < x.length; j++) x[j] /= k;
    return x;
}

/** numpy.pad(x, (n, n), mode='reflect') */
function reflectPad(x, n) {
    var L = x.length, out = new Float32Array(L + 2 * n);
    out.set(x, n);
    for (var i = 0; i < n; i++) {
        out[n - 1 - i] = x[Math.min(L - 1, i + 1)] || 0;
        out[n + L + i] = x[Math.max(0, L - 2 - i)] || 0;
    }
    return out;
}

/** RVC's coarse pitch: mel-scaled f0 → 1..255 (0 Hz → 1). Int32 for BigInt64 packing. */
function coarse(f0) {
    var out = new Int32Array(f0.length);
    for (var i = 0; i < f0.length; i++) {
        var f = f0[i], m = 1;
        if (f > 0) {
            var mel = 1127 * Math.log(1 + f / 700);
            m = (mel - MEL_MIN) * 254 / (MEL_MAX - MEL_MIN) + 1;
            if (m <= 1) m = 1; else if (m > 255) m = 255;
        }
        out[i] = Math.round(m);
    }
    return out;
}

/** f0 for the generator: unvoiced frames to 0, shifted by `semitones`, length `frames`. */
function prepareF0(f0, conf, semitones, frames) {
    var k = Math.pow(2, (semitones || 0) / 12), out = new Float32Array(frames);
    for (var i = 0; i < frames && i < f0.length; i++) out[i] = (conf && conf[i] < UNVOICED_CONF) ? 0 : f0[i] * k;
    return out;
}

/** Median f0 of the confidently voiced frames (conf ≥ 0.5): the source's speaking pitch, or 0. */
function voicedMedian(f0, conf) {
    var v = [];
    for (var i = 0; i < f0.length; i++) if (f0[i] > 0 && (!conf || conf[i] >= 0.5)) v.push(f0[i]);
    if (v.length < 10) return 0;
    v.sort(function (a, b) { return a - b; });
    return v[v.length >> 1];
}

/**
 * Where the voice's pitch goes: the SAME rule as the server lane
 * (VoiceGenderAiManager::semitoneFor) — round(12·log2(target/source)), clamped to ±12,
 * and 0 when the source pitch is not a speaking voice (50–500 Hz). PHP's round() is
 * half away from zero; Math.round is not — written out so the two lanes never disagree.
 */
function autoSemitones(sourceF0, targetF0) {
    if (!(sourceF0 >= 50 && sourceF0 <= 500) || !(targetF0 > 0)) return 0;
    var x = 12 * Math.log(targetF0 / sourceF0) / Math.LN2;
    var r = x < 0 ? -Math.round(-x) : Math.round(x);
    return Math.max(-12, Math.min(12, r));
}

/** ContentVec frames (20 ms) → generator frames (10 ms): each frame twice. */
function repeat2(feats, T, D) {
    var out = new Float32Array(T * 2 * D);
    for (var t = 0; t < T; t++) {
        var src = feats.subarray(t * D, (t + 1) * D);
        out.set(src, 2 * t * D); out.set(src, (2 * t + 1) * D);
    }
    return out;
}

// ── Noise as a function of ABSOLUTE position (counter-based: murmur3's finalizer, Box–Muller
// on two hashes), so two windows that overlap draw the SAME noise there and a crossfade joins
// two renderings of one signal. = vcbench/vg/vc_noise.normal_at, bit for bit.
// Streams: 0 — the prior [1,192,L] (index frame·192 + channel), 1 — the NSF excitation
// [1,L·upp,1] (index = output sample).
function fmix32(h) {
    h ^= h >>> 16; h = Math.imul(h, 0x85EBCA6B);
    h ^= h >>> 13; h = Math.imul(h, 0xC2B2AE35);
    h ^= h >>> 16;
    return h >>> 0;
}
function noiseKeys(seed, stream) {
    var k1 = fmix32((Math.imul(seed >>> 0, 0x9E3779B1) + Math.imul(stream, 0x632BE5AB)) >>> 0);
    return [k1, fmix32((k1 ^ 0x5BD1E995) >>> 0)];
}
function normalAtKeys(k, i) {
    var a = fmix32((fmix32((i ^ k[0]) >>> 0) + k[1]) >>> 0);
    var b = fmix32((fmix32((i ^ k[1]) >>> 0) + k[0]) >>> 0);
    return Math.sqrt(-2 * Math.log((a + 0.5) / 4294967296)) * Math.cos(2 * Math.PI * (b + 0.5) / 4294967296);
}
/** prior noise for frames [a, a+L), layout [192][L] */
function noisePrior(seed, a, L) {
    var k = noiseKeys(seed, 0), out = new Float32Array(192 * L);
    for (var c = 0; c < 192; c++) for (var t = 0; t < L; t++) out[c * L + t] = normalAtKeys(k, (a + t) * 192 + c);
    return out;
}
/** NSF excitation noise for output samples [a·upp, (a+L)·upp) */
function noiseNsf(seed, a, L, upp) {
    var k = noiseKeys(seed, 1), n = L * upp, s0 = a * upp, out = new Float32Array(n);
    for (var j = 0; j < n; j++) out[j] = normalAtKeys(k, s0 + j);
    return out;
}

/**
 * The NSF sine's phase (cycles) at the start of frame `a`, accumulated exactly as the
 * generator graph does it (rvc_export_gen.py gen_sine: per frame f0/sr·upp in float32,
 * wrapped to [-0.5, 0.5)). Fed as `phase0`, it continues the excitation across windows
 * instead of restarting it at 0 (seam_exp.py: the restart left clicks up to 7.7 dB).
 */
function phaseAt(f0, a, upp, sr) {
    var sum = 0;
    for (var i = 0; i < a && i < f0.length; i++) {
        var inc = Math.fround(Math.fround(f0[i] / sr) * upp);
        var r = (inc + 0.5) % 1; if (r < 0) r += 1;
        sum += r - 0.5;
    }
    var p = sum % 1;
    return p;
}

/**
 * Windows for long input, in 10 ms frames of the PADDED signal: each window converts
 * [a, b) and keeps [s, e) — `ctx` frames of context on each side are cut away so the
 * window edge never reaches the output. Neighbours are joined by a XF_S linear crossfade
 * centred on the seam (crossfadeWeight). Measured 18.10 (seam_exp.py, 25 seams of a 38 s
 * file): energy above 4 kHz at the seam over its surroundings, against the whole-file run —
 * hard cut with the phase restarted per window: median +1.3 dB, worst +7.7; continuous phase
 * + position noise + 20 ms crossfade: −0.6 / +3.2. Moving seams into pauses made it worse.
 */
var XF_S = 0.02;
function crossfadeWeight(j, sStart, sEnd, h, first, last) {
    // j, sStart, sEnd in output samples; h = half the crossfade
    if (!first && j < sStart + h) return j < sStart - h ? 0 : (j - (sStart - h) + 0.5) / (2 * h);
    if (!last && j >= sEnd - h) return j >= sEnd + h ? 0 : 1 - (j - (sEnd - h) + 0.5) / (2 * h);
    return 1;
}
function planWindows(frames, coreFrames, ctxFrames) {
    var out = [];
    coreFrames = coreFrames || 2000; ctxFrames = ctxFrames || 100;
    for (var s = 0; s < frames; s += coreFrames) {
        var e = Math.min(frames, s + coreFrames);
        out.push({ a: Math.max(0, s - ctxFrames), b: Math.min(frames, e + ctxFrames), s: s, e: e });
    }
    return out;
}

// ── Voice packs (vcbench/vg/make_vpk.py): ONE immutable file per model = ONNX bytes + the
// known-answer fixtures that decide whether this device computes it correctly + metadata.
//   'TVPK' 01 00 00 00 | u32 LE header length | JSON header | pad to 16 | blobs (16-aligned)
var PACK_MAGIC = [0x54, 0x56, 0x50, 0x4B, 1, 0, 0, 0];
var KAT_SEED = 20261018;

function halfToFloat(h) {
    var s = (h & 0x8000) ? -1 : 1, e = (h >> 10) & 0x1f, f = h & 0x3ff;
    if (e === 0) return s * Math.pow(2, -14) * (f / 1024);
    if (e === 31) return f ? NaN : s * Infinity;
    return s * Math.pow(2, e - 15) * (1 + f / 1024);
}

/** → { header, model: Uint8Array (a view, no copy), kat: { name: Float32Array } } */
function readPack(buf) {
    var u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
    for (var i = 0; i < 8; i++) if (u8[i] !== PACK_MAGIC[i]) { var e = new Error('not a voice pack'); e.code = 'pack_invalid'; throw e; }
    var dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    var n = dv.getUint32(8, true);
    var header = JSON.parse(new TextDecoder('utf-8').decode(u8.subarray(12, 12 + n)));
    var base = 12 + n; base += (16 - base % 16) % 16;
    var m = header.model, width = { f32: 4, f16: 2, i16: 2 };
    var fits = function (off, len) { return off >= 0 && len >= 0 && base + off + len <= u8.byteLength; };
    var bad = !fits(m.offset, m.length);
    Object.keys(header.kat || {}).forEach(function (k) { var d = header.kat[k]; if (!width[d.dtype] || !fits(d.offset, d.length * width[d.dtype])) bad = true; });
    if (bad) { var t = new Error('voice pack truncated'); t.code = 'pack_truncated'; throw t; }
    var kat = {};
    Object.keys(header.kat || {}).forEach(function (k) {
        var d = header.kat[k], at = base + d.offset, out = new Float32Array(d.length), j;
        if (d.dtype === 'f32') out.set(new Float32Array(u8.buffer.slice(u8.byteOffset + at, u8.byteOffset + at + 4 * d.length)));
        else if (d.dtype === 'f16') for (j = 0; j < d.length; j++) out[j] = halfToFloat(dv.getUint16(at + 2 * j, true));
        else if (d.dtype === 'i16') { var sc = d.scale || 1; for (j = 0; j < d.length; j++) out[j] = dv.getInt16(at + 2 * j, true) * sc; }
        kat[k] = out;
    });
    return { header: header, model: u8.subarray(base + m.offset, base + m.offset + m.length), kat: kat };
}

/**
 * The generator's known-answer inputs, from the ContentVec pack's STORED features and f0
 * (never from this device's own ContentVec output — a generator verdict must not inherit
 * the other network's error) and the page's noise stream. = make_vpk.kat_generator_inputs.
 */
function katGeneratorInputs(cvKat, upp, seed) {
    var D = 768, T = cvKat.feats.length / D, f0 = cvKat.f0;
    var L = Math.min(f0.length, 2 * T);
    var phone = repeat2(cvKat.feats, T, D).subarray(0, L * D);
    var s = seed || KAT_SEED;
    var nsff0 = new Float32Array(f0.subarray(0, L));
    return { phone: phone, L: L, pitch: coarse(nsff0), nsff0: nsff0, rnd: noisePrior(s, 0, L), nsfnz: noiseNsf(s, 0, L, upp), phase0: 0 };
}

var api = {
    SR_IN: SR_IN, HOP: HOP, PAD_S: PAD_S, UNVOICED_CONF: UNVOICED_CONF, KAT_SEED: KAT_SEED,
    highpass: highpass, peakLimit: peakLimit, reflectPad: reflectPad, coarse: coarse,
    prepareF0: prepareF0, repeat2: repeat2, planWindows: planWindows, voicedMedian: voicedMedian, autoSemitones: autoSemitones,
    noisePrior: noisePrior, noiseNsf: noiseNsf, phaseAt: phaseAt, XF_S: XF_S, crossfadeWeight: crossfadeWeight,
    readPack: readPack, katGeneratorInputs: katGeneratorInputs, halfToFloat: halfToFloat
};
root.VcShared = api;
if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof self !== 'undefined' ? self : this);
